import { Component, OnDestroy, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Subscription } from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { AppState, CorpMemberState } from '../ngrx-store/app.state'
import { profileUpdateSuccess } from '../ngrx-store/actions/corp-member.actions'
import { CallerService } from '../services/caller.service'
import { onboardingBarVisible } from '../utils/onboarding.util'

/**
 * One-line "Finish setting up" bar in NavComponent, hidden once onboarding is complete - the
 * wishlist and a first name (onboardingComplete()); after that the checklist doubles as the
 * wishlist, reachable from the nav menu. It opens on demand
 * (OnboardingChecklistDialogComponent - a bottom sheet on small screens, a dialog otherwise),
 * so it never takes page space. It also auto-opens once per corp member
 * (`onboarding_prompt_shown_at`), and "Remind me later" hides everything until
 * `onboarding_snoozed_until` - both stored on the backend so they hold across devices.
 * While it shows, the email verification banner waits (one bar at a time).
 */
@Component({
    selector: 'app-onboarding-checklist',
    templateUrl: './onboarding-checklist.component.html',
    styleUrls: ['./onboarding-checklist.component.css'],
})
export class OnboardingChecklistComponent implements OnInit, OnDestroy {
    isVisible = false

    private isOpen = false
    private hasAutoOpened = false
    private subscription: Subscription = new Subscription()

    constructor(
        private store: Store<AppState>,
        private callerService: CallerService,
    ) {}

    ngOnInit(): void {
        this.subscription.add(
            this.store.pipe(select('corper')).subscribe((corper: CorpMemberState) => {
                this.isVisible = onboardingBarVisible(corper)

                if (this.isVisible && !corper.onboarding_prompt_shown_at && !this.hasAutoOpened) {
                    this.hasAutoOpened = true
                    // Next tick, so the dialog isn't opened mid change detection of the nav.
                    setTimeout(() => this.autoOpen())
                }
            }),
        )
    }

    open(): void {
        if (this.isOpen) {
            return
        }
        this.isOpen = true
        this.subscription.add(
            this.callerService.openOnboardingChecklist().subscribe(() => {
                this.isOpen = false
            }),
        )
    }

    private autoOpen(): void {
        this.open()
        this.callerService.markOnboardingPromptShown().subscribe({
            next: (res: any) => this.store.dispatch(profileUpdateSuccess({ data: res.body.data })),
            // Not worth bothering the user about - worst case it auto-opens once more next visit.
            error: (err: HttpErrorResponse) => console.warn('Failed to mark onboarding prompt shown', err),
        })
    }

    /** Hides the bar right away - never waits on the server (see remindAboutOnboardingLater). */
    snooze(): void {
        this.callerService.remindAboutOnboardingLater()
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe()
    }
}
